import { MaterialIcons } from '@expo/vector-icons';
import { DrawerActions, useIsFocused } from '@react-navigation/native';
import { Stack, useNavigation, usePathname, useRouter } from 'expo-router';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Modal, Platform, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import EngagementItem from '@/components/EngagementItem';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useBackgroundEngagements, useBackgroundMyActiveEngagements } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';
import { Engagement } from '@/types/engagement';
import { calculateMapRegionFromFeatureCollection, extractPolygonsFromFeatureCollection } from '@/utils/geojson';
import { calculatePolygonCenter } from '@/utils/geometry';

// Lazy load the map component for better performance
const CrossPlatformMapView = lazy(() => import('@/components/MapView'));

interface CanvassBattlePlanDetailsScreenProps {
  battlePlanId: string;
  title?: string;
  myRoutesTitle?: string;
  noEngagementsMessage?: string;
  showPullToRefresh?: boolean;
}

export default function CanvassBattlePlanDetailsScreen({ 
  battlePlanId, 
  title = "Canvassing Walk Routes",
  myRoutesTitle = "My Routes",
  noEngagementsMessage = "No engagements found.",
  showPullToRefresh = true
}: CanvassBattlePlanDetailsScreenProps) {
  const [geometry, setGeometry] = useState<any>(null);
  const [tab, setTab] = useState<'list' | 'map'>('list');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'my-routes': true,
    'unassigned-routes': true,
    'assigned-routes': false,
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const { getSelectedBranch, getEngagementFilterSettings, setEngagementFilter, setEngagementSort } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const insets = useSafeAreaInsets();

  // Get filter/sort settings from Zustand store
  const filterSettings = getEngagementFilterSettings(battlePlanId);
  const filterType = filterSettings.filterType;
  const sortType = filterSettings.sortType;

  // Handlers to update filter and sort in Zustand store
  const handleFilterChange = useCallback((newFilterType: 'all' | 'unassigned' | 'assigned' | 'my-routes') => {
    setEngagementFilter(battlePlanId, newFilterType);
  }, [battlePlanId, setEngagementFilter]);

  const handleSortChange = useCallback((newSortType: 'number' | 'priority') => {
    setEngagementSort(battlePlanId, newSortType);
  }, [battlePlanId, setEngagementSort]);
  
  // Theme-aware colors for tabs
  const backgroundColor = useThemeColor({}, 'background');
  const borderColor = useThemeColor({}, 'border' as any);
  const cardBackground = useThemeColor({}, 'cardBackground' as any);
  const tintColor = useThemeColor({}, 'tint');
  const iconColor = useThemeColor({}, 'icon');
  
  // Use background fetch hooks for engagements (include electoral division ids for listing)
  const { data: allEngagements, loading: allEngagementsLoading, error: allEngagementsError, refetch: refetchAllEngagements } = useBackgroundEngagements(battlePlanId, true);
  const { data: myEngagements, loading: myEngagementsLoading, error: myEngagementsError, refetch: refetchMyEngagements } = useBackgroundMyActiveEngagements();
  
  // Get battle plan type for GOTV logic
  const battlePlanType = useMemo(() => {
    const getBattlePlans = useCampaignStore.getState().getBattlePlans;
    const battlePlans = getBattlePlans();
    const currentBattlePlan = battlePlans?.find(bp => bp.battle_plan_id === battlePlanId);
    return currentBattlePlan?.battle_plan_type;
  }, [battlePlanId]);
  
  // Determine overall loading and error states
  const loading = allEngagementsLoading || myEngagementsLoading;
  const error = allEngagementsError || myEngagementsError;
  
  // Refresh state for pull-to-refresh
  const [refreshing, setRefreshing] = useState(false);

  const isGOTV = battlePlanType === 'gotv-door-knocking' || battlePlanType === 'gotv-phoning';
  
  // Refresh handler for pull-to-refresh
  const handleRefresh = async () => {
    if (!showPullToRefresh) return;
    
    try {
      setRefreshing(true);
      // Refresh both engagement data sources
      await Promise.all([
        refetchAllEngagements(),
        refetchMyEngagements(),
      ]);
    } catch (error) {
      console.warn('Failed to refresh engagements:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const router = useRouter();
  const navigation = useNavigation();
  const pathname = usePathname();
  const isFocused = useIsFocused();

  // Platform-aware color utility
  const getPlatformColor = (color: string, opacity: number = 0.3) => {
    if (Platform.OS === 'web') {
      // For web, return solid colors (opacity handled by fillOpacity in Leaflet)
      return color;
    } else {
      // For native, return rgba colors with transparency
      const colorMap: { [key: string]: string } = {
        '#4CAF50': `rgba(76, 175, 80, ${opacity})`,
        '#FF9800': `rgba(255, 152, 0, ${opacity})`,
        '#F44336': `rgba(244, 67, 54, ${opacity})`,
        '#9E9E9E': `rgba(158, 158, 158, ${opacity})`,
        '#808080': `rgba(128, 128, 128, ${opacity})`,
        '#007AFF': `rgba(0, 122, 255, ${opacity})`,
      };
      return colorMap[color] || color;
    }
  };

  // Load geometry from store for all engagements (deferred to not block initial render)
  useEffect(() => {
    const loadGeometryFromStore = async () => {
      if (!isFocused || !allEngagements || allEngagements.length === 0) {
        return;
      }
      
      // Defer geometry loading to avoid blocking initial render
      const loadGeometry = async () => {
        try {
          const store = useCampaignStore.getState();
          const geometryFeatures = [];
          
          // Check which engagements need geometry data
          const engagementIds = allEngagements.map((engagement: any) => engagement.engagement_id);
          const uncachedEngagementIds = engagementIds.filter((id: string) => !store.getEngagementMapDetails(id));
          
          // Fetch geometry for all uncached engagements in a single call
          if (uncachedEngagementIds.length > 0) {
            try {
              await store.fetchEngagementMapDetails(uncachedEngagementIds, battlePlanId);
            } catch (err) {
              console.warn('Error fetching bulk geometry (may be expected during background fetch):', err);
            }
          }
          
          // Collect all geometry features from cache
          for (const engagement of allEngagements) {
            const mapDetails = store.getEngagementMapDetails(engagement.engagement_id);
            if (mapDetails?.geometry?.features) {
              geometryFeatures.push(...mapDetails.geometry.features);
            }
          }
          
          // Create combined geometry object
          if (geometryFeatures.length > 0) {
            setGeometry({
              type: 'FeatureCollection',
              features: geometryFeatures
            });
          }
        } catch (err) {
          console.warn('Error loading geometry from store (may be expected during background fetch):', err);
        }
      };

      // Use setTimeout to defer geometry loading until after initial render
      const timeoutId = setTimeout(loadGeometry, 0);
      return () => clearTimeout(timeoutId);
    };
    
    loadGeometryFromStore();
  }, [isFocused, battlePlanId, allEngagements]);

  // Compute map region from polygons or fallback
  const initialRegion = useMemo(() => {
    return calculateMapRegionFromFeatureCollection(geometry, 0.1);
  }, [geometry]);

  // Utility function to get progress-based colors
  const getProgressColors = (engagement: Engagement) => {
    // For GOTV knocking plans, use response zone data
    if (isGOTV) {
      const rzTotal = engagement.rz_total || 0;
      const rzWillNotVote = engagement.rz_will_not_vote || 0;
      const pledges = rzTotal - rzWillNotVote;
      const votersVoted = engagement.rz_voted || 0;
      const votersContacted = (engagement.rz_promised_to_vote || 0) + (engagement.rz_voters_not_in || 0);
      const votersNotYetContacted = pledges - votersContacted - votersVoted;
      // Treat NaN as 0
      const safeVotersNotYetContacted = isNaN(votersNotYetContacted) ? 0 : votersNotYetContacted;
      
      if (votersContacted + votersVoted === 0) {
        return {
          fillColor: getPlatformColor('#808080', 0.3), // Grey for no voters
          strokeColor: '#808080'
        };
      } else if (safeVotersNotYetContacted < 5) {
        return {
          fillColor: getPlatformColor('#4CAF50', 0.3), // Green
          strokeColor: '#4CAF50'
        };
      } else if (safeVotersNotYetContacted <= 50) {
        return {
          fillColor: getPlatformColor('#FF9800', 0.3), // Yellow/Orange
          strokeColor: '#FF9800'
        };
      } else {
        return {
          fillColor: getPlatformColor('#F44336', 0.3), // Red
          strokeColor: '#F44336'
        };
      }
    } else {
      // Standard canvassing logic
      const combinedProgress = engagement.address_count > 0 ? ((engagement.addresses_visited + engagement.addresses_not_in) / engagement.address_count) * 100 : 0;
      
      if (combinedProgress >= 80) {
        return {
          fillColor: getPlatformColor('#4CAF50', 0.3), // Green
          strokeColor: '#4CAF50' // Green border
        };
      } else if (combinedProgress >= 50) {
        return {
          fillColor: getPlatformColor('#FF9800', 0.3), // Orange
          strokeColor: '#FF9800' // Orange border
        };
      } else if (combinedProgress > 0) {
        return {
          fillColor: getPlatformColor('#F44336', 0.3), // Red
          strokeColor: '#F44336' // Red border
        };
      } else {
        return {
          fillColor: getPlatformColor('#9E9E9E', 0.3), // Gray
          strokeColor: '#424242' // Dark gray border for better visibility
        };
      }
    }
  };

  // Calculate priority for an engagement (higher = higher priority)
  const calculatePriority = useCallback((engagement: Engagement): number => {
    const isGOTV = engagement.battle_plan_type === 'gotv-door-knocking' || engagement.battle_plan_type === 'gotv-phoning';
    
    if (isGOTV) {
      // For GOTV: pledges left = voters not yet contacted
      const rzTotal = engagement.rz_total || 0;
      const rzWillNotVote = engagement.rz_will_not_vote || 0;
      const pledges = rzTotal - rzWillNotVote;
      const votersVoted = engagement.rz_voted || 0;
      const votersContacted = (engagement.rz_promised_to_vote || 0) + (engagement.rz_voters_not_in || 0);
      const votersNotYetContacted = pledges - votersContacted - votersVoted;
      // Treat NaN as 0
      return isNaN(votersNotYetContacted) ? 0 : votersNotYetContacted;
    } else {
      // For regular canvassing: addresses not visited
      const addressCount = engagement.address_count || 0;
      const addressesVisited = engagement.addresses_visited || 0;
      const addressesNotIn = engagement.addresses_not_in || 0;
      const addressesNotVisited = addressCount - addressesVisited - addressesNotIn;
      // Treat NaN as 0
      return isNaN(addressesNotVisited) ? 0 : addressesNotVisited;
    }
  }, []);

  // Pre-filter engagements to optimize listData creation
  const myBattlePlanEngagements = useMemo(() => {
    const filtered = myEngagements?.filter((engagement: Engagement) => 
      engagement.battle_plan_id === battlePlanId
    ) || [];
    
    // Sort based on sortType
    return filtered.sort((a: Engagement, b: Engagement) => {
      if (sortType === 'priority') {
        const priorityA = calculatePriority(a);
        const priorityB = calculatePriority(b);
        // Higher priority first (descending)
        return priorityB - priorityA;
      } else {
        // Sort by number_within_battle_plan (ascending)
        return a.number_within_battle_plan - b.number_within_battle_plan;
      }
    });
  }, [myEngagements, battlePlanId, sortType, calculatePriority]);

  // Split other engagements into unassigned and assigned based on active campaigners
  const unassignedEngagements = useMemo(() => {
    if (!allEngagements || !myEngagements) return [];
    
    const myEngagementIds = new Set(myBattlePlanEngagements.map(eng => eng.engagement_id));
    const filtered = allEngagements.filter((engagement: Engagement) => {
      const isNotMyEngagement = !myEngagementIds.has(engagement.engagement_id);
      const hasNoActiveCampaigners = !engagement.campaigners_active || engagement.campaigners_active.length === 0;
      return isNotMyEngagement && hasNoActiveCampaigners;
    });
    
    // Sort based on sortType
    return filtered.sort((a: Engagement, b: Engagement) => {
      if (sortType === 'priority') {
        const priorityA = calculatePriority(a);
        const priorityB = calculatePriority(b);
        // Higher priority first (descending)
        return priorityB - priorityA;
      } else {
        // Sort by number_within_battle_plan (ascending)
        return a.number_within_battle_plan - b.number_within_battle_plan;
      }
    });
  }, [allEngagements, myEngagements, myBattlePlanEngagements, sortType, calculatePriority]);

  const assignedEngagements = useMemo(() => {
    if (!allEngagements || !myEngagements) return [];
    
    const myEngagementIds = new Set(myBattlePlanEngagements.map(eng => eng.engagement_id));
    const filtered = allEngagements.filter((engagement: Engagement) => {
      const isNotMyEngagement = !myEngagementIds.has(engagement.engagement_id);
      const hasActiveCampaigners = engagement.campaigners_active && engagement.campaigners_active.length > 0;
      return isNotMyEngagement && hasActiveCampaigners;
    });
    
    // Sort based on sortType
    return filtered.sort((a: Engagement, b: Engagement) => {
      if (sortType === 'priority') {
        const priorityA = calculatePriority(a);
        const priorityB = calculatePriority(b);
        // Higher priority first (descending)
        return priorityB - priorityA;
      } else {
        // Sort by number_within_battle_plan (ascending)
        return a.number_within_battle_plan - b.number_within_battle_plan;
      }
    });
  }, [allEngagements, myEngagements, myBattlePlanEngagements, sortType, calculatePriority]);

  // Expand assigned routes if it's the only section with engagements
  useEffect(() => {
    const hasMyRoutes = myBattlePlanEngagements.length > 0;
    const hasUnassigned = unassignedEngagements.length > 0;
    const hasAssigned = assignedEngagements.length > 0;
    
    // If assigned routes is the only section with engagements, expand it
    if (hasAssigned && !hasMyRoutes && !hasUnassigned) {
      setExpandedSections(prev => ({
        ...prev,
        'assigned-routes': true,
      }));
    }
  }, [myBattlePlanEngagements.length, unassignedEngagements.length, assignedEngagements.length]);

  // Convert geometry to MapPolygon[] and create markers for engagements
  // Only compute when map tab is active to improve performance
  const polygons: any[] = useMemo(() => {
    // Early return if not on map tab - avoid expensive computation
    if (tab !== 'map' || !geometry || !geometry.features || !allEngagements) return [];
    
    // Get filtered engagements (will be properly defined after engagement arrays)
    let filteredEngagements: Engagement[] = [];
    switch (filterType) {
      case 'my-routes':
        filteredEngagements = myBattlePlanEngagements;
        break;
      case 'unassigned':
        filteredEngagements = unassignedEngagements;
        break;
      case 'assigned':
        filteredEngagements = assignedEngagements;
        break;
      case 'all':
      default:
        filteredEngagements = allEngagements;
    }
    
    if (!filteredEngagements || filteredEngagements.length === 0) return [];

    // Filter geometry features to only include filtered engagements
    const filteredEngagementIds = new Set(filteredEngagements.map((eng: Engagement) => eng.engagement_id));
    const filteredFeatures = geometry.features.filter((feature: any) => 
      filteredEngagementIds.has(feature.properties?.engagement_id)
    );

    return extractPolygonsFromFeatureCollection(
      { type: 'FeatureCollection', features: filteredFeatures },
      {
        idPrefix: 'polygon',
        strokeColor: '#007AFF',
        fillColor: getPlatformColor('#007AFF', 0.2),
        strokeWidth: 2,
        propertyExtractor: (feature, index) => {
          const engagement = filteredEngagements.find((eng: Engagement) => 
            eng.engagement_id === feature.properties?.engagement_id
          );
          return feature.id || `engagement-${engagement?.engagement_id || 'unknown'}-${index}`;
        },
        styleExtractor: (feature) => {
          const engagement = filteredEngagements.find((eng: Engagement) => 
            eng.engagement_id === feature.properties?.engagement_id
          );
          return engagement ? getProgressColors(engagement) : {
            fillColor: getPlatformColor('#007AFF', 0.2),
            strokeColor: '#007AFF'
          };
        },
        onPress: (feature) => {
          const engagement = filteredEngagements.find((eng: Engagement) => 
            eng.engagement_id === feature.properties?.engagement_id
          );
          if (engagement) {
            // Navigate to engagement screen (same for all users)
            router.push({
              pathname: './[battlePlanId]/[engagementId]',
              params: { 
                battlePlanId: battlePlanId,
                engagementId: engagement.engagement_id 
              }
            });
          }
        },
      }
    );
  }, [tab, geometry, allEngagements, filterType, myBattlePlanEngagements, unassignedEngagements, assignedEngagements, battlePlanId, router, battlePlanType]);

  // Create markers for active campaigner counts and route numbers
  // Only compute when map tab is active to improve performance
  const markers: any[] = useMemo(() => {
    // Early return if not on map tab - avoid expensive computation
    if (tab !== 'map' || !geometry || !geometry.features || !allEngagements) return [];
    
    // Get filtered engagements
    let filteredEngagements: Engagement[] = [];
    switch (filterType) {
      case 'my-routes':
        filteredEngagements = myBattlePlanEngagements;
        break;
      case 'unassigned':
        filteredEngagements = unassignedEngagements;
        break;
      case 'assigned':
        filteredEngagements = assignedEngagements;
        break;
      case 'all':
      default:
        filteredEngagements = allEngagements;
    }
    
    if (!filteredEngagements || filteredEngagements.length === 0) return [];
    
    // Filter geometry features to only include filtered engagements
    const filteredEngagementIds = new Set(filteredEngagements.map((eng: Engagement) => eng.engagement_id));
    
    return geometry.features
      .filter((feature: any) => 
        feature.geometry && 
        feature.geometry.type === 'Polygon' &&
        filteredEngagementIds.has(feature.properties?.engagement_id)
      )
      .map((feature: any, index: number) => {
      const engagement = filteredEngagements.find((eng: Engagement) => 
        eng.engagement_id === feature.properties?.engagement_id
      );
      
      if (!engagement) return null;
      
      // Calculate proper center of polygon using improved geometry utility
      const rawCoordinates = feature.geometry.coordinates[0];
      
      // Ensure coordinates is a valid array of [number, number] pairs
      if (!Array.isArray(rawCoordinates) || rawCoordinates.length < 3) return null;
      
      // Validate that each coordinate is a valid [number, number] pair
      const coordinates: [number, number][] = rawCoordinates.filter((coord): coord is [number, number] => 
        Array.isArray(coord) && 
        coord.length >= 2 && 
        typeof coord[0] === 'number' && 
        typeof coord[1] === 'number' &&
        !isNaN(coord[0]) && 
        !isNaN(coord[1])
      );
      
      if (coordinates.length < 3) return null; // Need at least 3 points for a valid polygon
      
      let center;
      try {
        center = calculatePolygonCenter(coordinates);
        if (!center || typeof center.latitude !== 'number' || typeof center.longitude !== 'number') {
          return null;
        }
      } catch (error) {
        console.warn(`Error calculating center for engagement ${engagement.engagement_id}:`, error);
        return null;
      }
      
      // Use feature.id if available, otherwise use engagement_id and index for uniqueness
      const markerId = feature.id || `marker-engagement-${engagement.engagement_id}-${index}`;
      
      // Always show route number, but style differently based on active campaigners
      const hasActiveCampaigners = engagement.campaigners_active && engagement.campaigners_active.length > 0;
      
      return {
        id: `route-number-${markerId}`,
        coordinate: center,
        title: String(engagement.number_within_battle_plan),
        description: hasActiveCampaigners 
          ? `${engagement.campaigners_active.length} active campaigner${engagement.campaigners_active.length === 1 ? '' : 's'}`
          : `Route ${engagement.number_within_battle_plan}`,
        campaignerNames: engagement.active_campaigner_names || [],
        engagementId: engagement.engagement_id,
        routeName: engagement.engagement_name || `Route ${engagement.number_within_battle_plan}`,
        routeNumber: engagement.number_within_battle_plan,
        type: hasActiveCampaigners ? 'route-number-active' as const : 'route-number' as const,
      };
    }).filter(Boolean);
  }, [tab, geometry, allEngagements, filterType, myBattlePlanEngagements, unassignedEngagements, assignedEngagements]);

  // Memoize the engagement press handler to prevent unnecessary re-renders
  const handleEngagementPress = useCallback((engagement: Engagement) => {
    router.push({
      pathname: './[battlePlanId]/[engagementId]',
      params: { 
        battlePlanId: battlePlanId,
        engagementId: engagement.engagement_id 
      }
    });
  }, [router, battlePlanId]);

  const renderEngagement = useCallback(({ item: engagement }: { item: Engagement }) => {
    return (
      <EngagementItem 
        engagement={engagement}
        onPress={handleEngagementPress}
        showProgressBars={true}
        showStats={true}
      />
    );
  }, [handleEngagementPress]);

  // Create combined data for FlatList with section headers (always show all sections for list view)
  const listData = useMemo(() => {
    const data: { type: 'header' | 'engagement', sectionId?: string, title?: string, engagement?: Engagement }[] = [];
    
    // Always show all sections in list view (filtering only applies to map view)
    
    // Add my routes section (only for this battle plan)
    if (myBattlePlanEngagements.length > 0) {
      data.push({ type: 'header', sectionId: 'my-routes', title: myRoutesTitle });
      if (expandedSections['my-routes']) {
        myBattlePlanEngagements.forEach((engagement: Engagement) => {
          data.push({ type: 'engagement', engagement });
        });
      }
    }
    
    // Add unassigned routes section
    if (unassignedEngagements.length > 0) {
      data.push({ type: 'header', sectionId: 'unassigned-routes', title: 'Unassigned Routes' });
      if (expandedSections['unassigned-routes']) {
        unassignedEngagements.forEach((engagement: Engagement) => {
          data.push({ type: 'engagement', engagement });
        });
      }
    }
    
    // Add assigned routes section
    if (assignedEngagements.length > 0) {
      data.push({ type: 'header', sectionId: 'assigned-routes', title: 'Assigned Routes' });
      if (expandedSections['assigned-routes']) {
        assignedEngagements.forEach((engagement: Engagement) => {
          data.push({ type: 'engagement', engagement });
        });
      }
    }
    
    return data;
  }, [myBattlePlanEngagements, unassignedEngagements, assignedEngagements, myRoutesTitle, expandedSections]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const renderListItem = useCallback(({ item }: { item: { type: 'header' | 'engagement', sectionId?: string, title?: string, engagement?: Engagement } }) => {
    if (item.type === 'header') {
      const sectionId = item.sectionId || '';
      const isExpanded = expandedSections[sectionId] ?? true;
      const engagementCount = sectionId === 'my-routes' 
        ? myBattlePlanEngagements.length 
        : sectionId === 'unassigned-routes'
        ? unassignedEngagements.length
        : assignedEngagements.length;
      
      return (
        <TouchableOpacity
          onPress={() => toggleSection(sectionId)}
          style={{ 
            paddingHorizontal: 16, 
            paddingVertical: 12, 
            backgroundColor: cardBackground,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <MaterialIcons 
              name={isExpanded ? 'expand-less' : 'expand-more'} 
              size={24} 
              color={iconColor}
              style={{ marginRight: 8 }}
            />
            <ThemedText style={AppStyles.headerText}>
              {item.title} ({engagementCount})
            </ThemedText>
          </View>
        </TouchableOpacity>
      );
    } else if (item.type === 'engagement' && item.engagement) {
      return renderEngagement({ item: item.engagement });
    }
    return null;
  }, [renderEngagement, cardBackground, expandedSections, toggleSection, myBattlePlanEngagements.length, unassignedEngagements.length, assignedEngagements.length, iconColor]);

  // Memoize keyExtractor for better FlatList performance
  const keyExtractor = useCallback((item: { type: 'header' | 'engagement', sectionId?: string, title?: string, engagement?: Engagement }) => {
    return item.type === 'header' ? `header-${item.sectionId || item.title}` : `engagement-${item.engagement?.engagement_id}`;
  }, []);

  if (loading) {
    return (
      <ThemedView style={AppStyles.container}>
        <ActivityIndicator size="large" color={iconColor} />
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={AppStyles.container}>
        <ThemedText style={AppStyles.errorText}>{error.message}</ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{ flex: 1 }}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>{title}</ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color={tintColor} />
            </TouchableOpacity>
          ),
          headerRight: () => (
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingRight: 8 }}>
              <TouchableOpacity 
                onPress={() => setShowFilterModal(true)} 
                style={{ paddingRight: 15, paddingLeft: 8 }}
              >
                <MaterialIcons name={tab === 'list' ? 'sort' : 'filter-list'} size={24} color={tintColor} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => router.push({ 
                pathname: '/(main)/[branch]/share', 
                params: { 
                  branch: selectedBranch?.branch_short_name || "", 
                  ref: pathname,
                  battlePlanId: battlePlanId
                } 
              })} style={{ paddingRight: 15 }}>
                <IconSymbol name="square.and.arrow.up" size={24} color={tintColor} />
              </TouchableOpacity>
            </View>
          ),
          headerBackVisible: false,
        }}
      />
      
      {/* Main Content Area */}
      <View style={{ flex: 1 }}>
        {/* Tab Content */}
        {loading && <ActivityIndicator size="large" color={iconColor} />}
        {error && <ThemedText style={AppStyles.errorText}>{error}</ThemedText>}
        {!loading && !error && listData.length === 0 && (
          <ThemedText>{noEngagementsMessage}</ThemedText>
        )}
        {!loading && !error && listData.length > 0 && tab === 'list' && (
          <FlatList
            data={listData}
            keyExtractor={keyExtractor}
            renderItem={renderListItem}
            contentContainerStyle={{ paddingBottom: insets.bottom }}
            {...(showPullToRefresh && {
              onRefresh: handleRefresh,
              refreshing: refreshing
            })}
          />
        )}

        {!loading && !error && allEngagements && allEngagements.length > 0 && tab === 'map' && (
          <View style={{ flex: 1, width: '100%' }}>
            {geometry && initialRegion ? (
              <Suspense fallback={
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <ActivityIndicator size="large" color={tintColor} />
                  <ThemedText style={{ marginTop: 8 }}>Loading map...</ThemedText>
                </View>
              }>
                <CrossPlatformMapView
                  initialRegion={initialRegion}
                  polygons={polygons}
                  markers={markers}
                  showUserLocation={true}
                  showProgressLegend={true}
                  battlePlanId={battlePlanId}
                  legendItems={isGOTV ? [
                    { color: '#4CAF50', label: '< 5 left' },
                    { color: '#FF9800', label: '5-50 left' },
                    { color: '#F44336', label: '50+ left' },
                    { color: '#808080', label: 'Not started' },
                  ] : [
                    { color: '#4CAF50', label: '80-100%' },
                    { color: '#FF9800', label: '50-79%' },
                    { color: '#F44336', label: '1-49%' },
                    { color: '#9E9E9E', label: '0%' },
                  ]}
                  style={{ flex: 1, width: '100%' }}
                />
              </Suspense>
            ) : (
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <ActivityIndicator size="large" color="#007AFF" />
                <ThemedText style={{ marginTop: 8 }}>Loading map data...</ThemedText>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Bottom Tab Navigation */}
    <View style={{
      flexDirection: 'row',
      backgroundColor: backgroundColor,
      borderTopWidth: 1,
      borderTopColor: borderColor,
      paddingBottom: insets.bottom,
    }}>
      <TouchableOpacity
        style={[
          {
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            justifyContent: 'center',
          },
          tab === 'list' && { backgroundColor: cardBackground }
        ]}
        onPress={() => setTab('list')}
      >
        <MaterialIcons 
          name="list" 
          size={24} 
          color={tab === 'list' ? tintColor : iconColor} 
        />
        <ThemedText style={[
          { fontSize: 12, marginTop: 4 },
          tab === 'list' ? { color: tintColor } : { color: iconColor }
        ]}>List</ThemedText>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          {
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            justifyContent: 'center',
          },
          tab === 'map' && { backgroundColor: cardBackground }
        ]}
        onPress={() => setTab('map')}
      >
        <MaterialIcons 
          name="map" 
          size={24} 
          color={tab === 'map' ? tintColor : iconColor} 
        />
        <ThemedText style={[
          { fontSize: 12, marginTop: 4 },
          tab === 'map' ? { color: tintColor } : { color: iconColor }
        ]}>Map</ThemedText>
      </TouchableOpacity>
    </View>

      {/* Filter Modal */}
      <Modal
        visible={showFilterModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowFilterModal(false)}
      >
        <TouchableOpacity 
          style={{ 
            flex: 1, 
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center'
          }} 
          activeOpacity={1} 
          onPress={() => setShowFilterModal(false)}
        >
          <View style={{ 
            backgroundColor: cardBackground,
            borderRadius: 12,
            padding: 20,
            minWidth: 280,
            maxWidth: '80%',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 5,
          }}>
            <View style={{ 
              flexDirection: 'row', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16
            }}>
              <ThemedText style={[AppStyles.headerText, { fontSize: 18 }]}>
                {tab === 'list' ? 'Sort' : 'Filter'}
              </ThemedText>
              <TouchableOpacity
                onPress={() => setShowFilterModal(false)}
                style={{ padding: 4 }}
              >
                <MaterialIcons name="close" size={24} color={iconColor} />
              </TouchableOpacity>
            </View>
            
            {/* Sort Section - Only show for list view */}
            {tab === 'list' && (
            <View style={{ marginBottom: 24 }}>
              <ThemedText style={[AppStyles.headerText, { fontSize: 14, marginBottom: 12, opacity: 0.7 }]}>Sort By</ThemedText>
              
              <TouchableOpacity
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 16,
                  borderRadius: 8,
                  backgroundColor: sortType === 'number' ? tintColor + '20' : 'transparent',
                  marginBottom: 8,
                }}
                onPress={() => {
                  handleSortChange('number');
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {sortType === 'number' && (
                    <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                  )}
                  <ThemedText style={{ 
                    fontSize: 16,
                    color: sortType === 'number' ? tintColor : undefined,
                    marginLeft: sortType === 'number' ? 0 : 28
                  }}>
                    Route Number
                  </ThemedText>
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 16,
                  borderRadius: 8,
                  backgroundColor: sortType === 'priority' ? tintColor + '20' : 'transparent',
                  marginBottom: 16,
                }}
                onPress={() => {
                  handleSortChange('priority');
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {sortType === 'priority' && (
                    <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                  )}
                  <ThemedText style={{ 
                    fontSize: 16,
                    color: sortType === 'priority' ? tintColor : undefined,
                    marginLeft: sortType === 'priority' ? 0 : 28
                  }}>
                    Priority
                  </ThemedText>
                </View>
              </TouchableOpacity>
            </View>
            )}

            {/* Filter Section - Only show for map view */}
            {tab === 'map' && (
            <View>
              <ThemedText style={[AppStyles.headerText, { fontSize: 14, marginBottom: 12, opacity: 0.7 }]}>Filter</ThemedText>
              
              <TouchableOpacity
              style={{
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: filterType === 'all' ? tintColor + '20' : 'transparent',
                marginBottom: 8,
              }}
              onPress={() => {
                handleFilterChange('all');
                setShowFilterModal(false);
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {filterType === 'all' && (
                  <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                )}
                <ThemedText style={{ 
                  fontSize: 16,
                  color: filterType === 'all' ? tintColor : undefined,
                  marginLeft: filterType === 'all' ? 0 : 28
                }}>
                  All Routes
                </ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={{
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: filterType === 'my-routes' ? tintColor + '20' : 'transparent',
                marginBottom: 8,
              }}
              onPress={() => {
                handleFilterChange('my-routes');
                setShowFilterModal(false);
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {filterType === 'my-routes' && (
                  <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                )}
                <ThemedText style={{ 
                  fontSize: 16,
                  color: filterType === 'my-routes' ? tintColor : undefined,
                  marginLeft: filterType === 'my-routes' ? 0 : 28
                }}>
                  My Routes
                </ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={{
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: filterType === 'unassigned' ? tintColor + '20' : 'transparent',
                marginBottom: 8,
              }}
              onPress={() => {
                handleFilterChange('unassigned');
                setShowFilterModal(false);
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {filterType === 'unassigned' && (
                  <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                )}
                <ThemedText style={{ 
                  fontSize: 16,
                  color: filterType === 'unassigned' ? tintColor : undefined,
                  marginLeft: filterType === 'unassigned' ? 0 : 28
                }}>
                  Unassigned Routes
                </ThemedText>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={{
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 8,
                backgroundColor: filterType === 'assigned' ? tintColor + '20' : 'transparent',
              }}
              onPress={() => {
                handleFilterChange('assigned');
                setShowFilterModal(false);
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                {filterType === 'assigned' && (
                  <MaterialIcons name="check" size={20} color={tintColor} style={{ marginRight: 8 }} />
                )}
                <ThemedText style={{ 
                  fontSize: 16,
                  color: filterType === 'assigned' ? tintColor : undefined,
                  marginLeft: filterType === 'assigned' ? 0 : 28
                }}>
                  Assigned Routes
                </ThemedText>
              </View>
            </TouchableOpacity>
            </View>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </ThemedView>
  );
}
